import { useContext, useEffect, useState, useCallback } from 'react';
import './App.css';

import { getUser } from './api/userRequest';
import toast, { Toaster } from 'react-hot-toast';
import LoadingBar from 'react-top-loading-bar';
import { Emoji } from 'emoji-picker-react';

import TopBar from './components/CommonTopBar/TopBar';
import WebPages from './components/Maincomp';

import { useLocation, useNavigate } from 'react-router-dom';
import { MainContext } from './context/MainContextProvider';

/* ---------------- ROUTE CONFIG ---------------- */

const EXCLUDED_ROUTES = [
  '/',
  '/login',
  '/signup',
  '/mlogin',
  '/msignup',
  '/about',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/faqs',
  '/careers',
];

const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/signup',
  '/mlogin',
  '/msignup',
  '/about',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/faqs',
  '/careers',
  '/resetpassword',
  '/password/reset', // handles /password/reset/:token automatically
  '/codeClash',
  '/codeClashquestion',
  '/resetPasswordEmail'
];

const AUTH_ROUTES = ['/','/login', '/signup', '/mlogin', '/msignup'];


/* ---------------- APP ---------------- */

function App() {
  const [userData, setUserData] = useState(null);
  const [Mentor, setMentor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSocietyMember, setIsSocietyMember] = useState(false);
  const [notifyList, setNotifyList] = useState([]);
  const [isFetched, setIsFetched] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const { progress, setProgress } = useContext(MainContext);

  /* ---------------- THEME INIT ---------------- */
  /* Runs once on app mount so dark mode (toggled in Settings) applies
     even when the current route's layout doesn't render SideNav. */
  useEffect(() => {
    document.body.classList.toggle(
      'dark-theme',
      localStorage.getItem('theme') === 'dark'
    );
  }, []);

  /* ---------------- ROUTE CHECKS ---------------- */

  const isPublicRoute = PUBLIC_ROUTES.some(
    route => pathname === route || pathname.startsWith(route + '/')
  );

  const shouldRenderTopBar = !EXCLUDED_ROUTES.includes(pathname);

  /* ---------------- REDIRECT HANDLER ---------------- */

  const redirectIfNotAuthorize = useCallback(() => {
    toast.error('Please Login/Signup to continue');

    if (window.innerWidth > 500) {
      navigate(`/login?redirect=${pathname}`, { replace: true });
    } else {
      navigate(`/mlogin?redirect=${pathname}`, { replace: true });
    }
  }, [navigate, pathname]);

  /* ---------------- AUTH EFFECT ---------------- */

useEffect(() => {
  const token = localStorage.getItem('skilloptoken');
  const expiry = localStorage.getItem('tokenExpiry');
  const isTokenValid =
    token && expiry && new Date() < new Date(expiry);

  // ✅ LOGGED-IN USER ON LOGIN/SIGNUP → REDIRECT
  if (isTokenValid && AUTH_ROUTES.includes(pathname)) {
    navigate('/homepage', { replace: true });
    return;
  }

  // ✅ PUBLIC ROUTES → allow without fetch
  if (isPublicRoute) {
    setIsFetched(true);
    return;
  }

  // ❌ PRIVATE ROUTE + INVALID TOKEN
  if (!isTokenValid) {
    localStorage.removeItem('skilloptoken');
    localStorage.removeItem('tokenExpiry');
    redirectIfNotAuthorize();
    return;
  }

  // ✅ FETCH USER FOR PRIVATE ROUTES
  const fetchUser = async () => {
    try {
      const { data } = await getUser();
      if (!data?.result) throw new Error();

      const user = data.result;
      setUserData(user);
      setMentor(user.isMentor);
      setIsAdmin(user.isAdmin);
      setIsSocietyMember(user.isSocietyMember);
      setNotifyList(user.notifications || []);
      setIsFetched(true);
    } catch {
      redirectIfNotAuthorize();
    }
  };

  fetchUser();
}, [pathname, isPublicRoute, redirectIfNotAuthorize, navigate]);

  /* ---------------- BLOCK RENDER UNTIL AUTH RESOLVES ---------------- */

  if (!isFetched && !isPublicRoute) {
    return null; // or loader component
  }

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <Toaster containerStyle={{ top: 90 }} />

      <div className="topNav">
        {shouldRenderTopBar && <TopBar />}
      </div>

      <WebPages
        userData={userData}
        setUserData={setUserData}
        Mentor={Mentor}
        setMentor={setMentor}
        isAdmin={isAdmin}
        setIsAdmin={setIsAdmin}
        isSocietyMember={isSocietyMember}
        setIsSocietyMember={setIsSocietyMember}
        notifyList={notifyList}
        setNotifyList={setNotifyList}
        isFetched={isFetched}
        setIsFetched={setIsFetched}
        setProgress={setProgress}
      />

      <LoadingBar
        color="#f11946"
        height={4}
        progress={progress}
        shadow
      />

      <Emoji />
    </>
  );
}

export default App;
